import { useCallback, useEffect, useState } from 'react'

import { getAllSkus } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { AllSkusResponseModel } from '@/types/clarityLens'
import { AllSkusModel } from '@/types/clarityLens/metrics/spendLens/priceHistory/allSkus.model'

const ALL_SKUS_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'SKU Name',
    accessorKey: 'skuName',
    type: 'text',
  },
  {
    header: 'Jan',
    accessorKey: 'January',
    type: 'money',
  },
  {
    header: 'Feb',
    accessorKey: 'February',
    type: 'money',
  },
  {
    header: 'Mar',
    accessorKey: 'March',
    type: 'money',
  },
  {
    header: 'Apr',
    accessorKey: 'April',
    type: 'money',
  },
  {
    header: 'May',
    accessorKey: 'May',
    type: 'money',
  },
  {
    header: 'Jun',
    accessorKey: 'June',
    type: 'money',
  },
  {
    header: 'Jul',
    accessorKey: 'July',
    type: 'money',
  },
  {
    header: 'Aug',
    accessorKey: 'August',
    type: 'money',
  },
  {
    header: 'Sep',
    accessorKey: 'September',
    type: 'money',
  },
  {
    header: 'Oct',
    accessorKey: 'October',
    type: 'money',
  },
  {
    header: 'Nov',
    accessorKey: 'November',
    type: 'money',
  },
  {
    header: 'Dec',
    accessorKey: 'December',
    type: 'money',
  },
]

export default function useAllSkus(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [allSkusData, setAllSkusData] = useState<AllSkusModel[]>()

  const fetchAllData = useCallback(async () => {
    if (!itemName) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const { data } = await fetchAllSkusData(
        branches,
        startDate,
        endDate,
        itemName
      )

      setAllSkusData(data)
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching sku breakdown information...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    allSkusData,
    allSkusColumns: ALL_SKUS_COLUMNS,
    loading,
    error,
    refetch,
  }
}

function convertAllSkusResponse(
  response: AllSkusResponseModel
): AllSkusModel[] {
  return Object.entries(response.all_skus).map(([skuName, months]) => ({
    skuName,
    ...months,
  }))
}

async function fetchAllSkusData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: AllSkusModel[]
}> {
  try {
    const response = await getAllSkus(branches, startDate, endDate, itemName)

    const data: AllSkusModel[] = convertAllSkusResponse(response)

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}
